import { IncentivesMarketPlanContext } from 'modules/incentivesMarket/types/IncentivesMarketBeneficiaryContext';

export type SellPlanBreakdownRow = {
  planId: string | null;
  beneficiaryId: string;
  planName: string;
  availableUnits: number;
  unitsSold: number;
  remainingUnits: number;
  sharePrice: number;
  sellingPrice: number;
  retentionPercent: number;
  result: number;
};

export type DistributeSellUnitsByPlanResult = {
  rows: SellPlanBreakdownRow[];
  total: number;
  totalRemaining: number;
};

const calculatePlanResult = ({
  unitsSold,
  sharePrice,
  sellingPrice,
  retentionPercent,
}: {
  unitsSold: number;
  sharePrice: number;
  sellingPrice: number;
  retentionPercent: number;
}): number => {
  const grossProfit = (sellingPrice - sharePrice) * unitsSold;
  const retentionAmount = (retentionPercent * grossProfit) / 100;
  return grossProfit - retentionAmount;
};

export const distributeSellUnitsByPlan = ({
  plans,
  totalUnits,
  sellingPrice,
  retentionPercent = 0,
}: {
  plans: IncentivesMarketPlanContext[];
  totalUnits: number;
  sellingPrice: number;
  retentionPercent?: number;
}): DistributeSellUnitsByPlanResult => {
  let remaining = Math.max(0, totalUnits);

  const rows: SellPlanBreakdownRow[] = plans.map((plan) => {
    const availableUnits = plan.availableUnits ?? 0;
    const unitsSold = Math.min(availableUnits, remaining);
    remaining -= unitsSold;

    const sharePrice = plan.sharePrice ?? 0;

    return {
      planId: plan.planId,
      beneficiaryId: plan.beneficiaryId,
      planName: plan.planName,
      availableUnits,
      unitsSold,
      remainingUnits: availableUnits - unitsSold,
      sharePrice,
      sellingPrice,
      retentionPercent,
      result: calculatePlanResult({
        unitsSold,
        sharePrice,
        sellingPrice,
        retentionPercent,
      }),
    };
  });

  const total = rows.reduce((sum, row) => sum + row.result, 0);
  const totalRemaining = rows.reduce((sum, row) => sum + row.remainingUnits, 0);

  return { rows, total, totalRemaining };
};
